// FirstScanNudge — a one-time coach-mark on the Result page pointing at
// the bottom-anchored Corridor toggle. Early adopters told us they
// scrolled right past that card because it sits below all the identify-
// value; this nudge scrolls the card into view and pins a small pointer
// pill above it the first time a user lands on any /plant/:id.
//
// Dismisses on: tap X, tap outside, scroll away, click the actual toggle
// (mission accomplished), or press Escape. Any dismissal is persisted to
// localStorage so it never shows again.
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, ArrowDown } from "lucide-react";

const LS_KEY = "prairie:corridor-nudge-seen";

function hasSeenBefore() {
  if (typeof window === "undefined") return true;
  try { return localStorage.getItem(LS_KEY) === "1"; } catch { return true; }
}

function markSeen() {
  try { localStorage.setItem(LS_KEY, "1"); } catch { /* private mode — silently ignore */ }
}

/**
 * @param anchorSelector - CSS selector for the element to point at
 *                        (defaults to the CorridorContributeCard).
 * @param enabled        - Parent gate. Result.jsx only renders us once
 *                        the scan is loaded and the corridor card is
 *                        mounted, so the pointer can find its anchor.
 */
export default function FirstScanNudge({
  anchorSelector = '[data-testid="corridor-contribute-card"]',
  enabled = true,
}) {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState(null); // {top, left, width}
  const rafRef = useRef(0);

  // One-shot mount: read the flag, find the anchor, scroll it into view,
  // then position ourselves. Bails silently if any step fails so a stray
  // race never blocks the page.
  useEffect(() => {
    if (!enabled) return;
    if (hasSeenBefore()) return;
    // Give the corridor card a chance to render (Result page loads scan
    // asynchronously). Two paint ticks is plenty in practice.
    let cancelled = false;
    const timer = setTimeout(() => {
      if (cancelled) return;
      const anchor = document.querySelector(anchorSelector);
      if (!anchor) return;
      // Scroll into view smoothly so the user's eye follows the pill's
      // entrance to the card, not the other way around.
      anchor.scrollIntoView({ behavior: "smooth", block: "center" });
      // Second tick lets scrollIntoView settle before we measure.
      setTimeout(() => {
        if (cancelled) return;
        setShow(true);
        recomputePosition(anchor);
      }, 380);
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [enabled, anchorSelector]);

  // Keep the pill anchored on resize / scroll. rAF-throttled so a fast
  // scroll doesn't churn state.
  useEffect(() => {
    if (!show) return;
    const anchor = document.querySelector(anchorSelector);
    if (!anchor) return;
    const schedule = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        const el = document.querySelector(anchorSelector);
        if (el) recomputePosition(el);
      });
    };
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, [show, anchorSelector]);

  const recomputePosition = (anchor) => {
    const rect = anchor.getBoundingClientRect();
    // If the anchor scrolled fully out of view (user paged past it),
    // dismiss instead of drifting the pill off-screen.
    const viewportH = window.innerHeight;
    if (rect.bottom < 0 || rect.top > viewportH) {
      dismiss("scrolled-away");
      return;
    }
    setPos({
      top: rect.top - 8,   // sit 8px above the card
      left: rect.left,
      width: rect.width,
    });
  };

  const dismiss = (_reason) => {
    setShow(false);
    markSeen();
  };

  // Dismiss when the user actually interacts with the toggle — they've
  // seen the target and clicked (or tried to click) something, which
  // is exactly the outcome we hoped for.
  useEffect(() => {
    if (!show) return;
    const onDocPointerDown = (e) => {
      if (!(e.target instanceof Element)) return;
      // Any click on the toggle itself is "mission accomplished".
      if (e.target.closest('[data-testid="corridor-contribute-toggle"]')) {
        dismiss("toggle-tap");
        return;
      }
      // Clicks inside the nudge itself (the X, or the pill body) are
      // handled by the buttons directly; ignore here.
      if (e.target.closest('[data-testid="corridor-nudge"]')) return;
      // Any other tap dismisses too — the user has moved on.
      dismiss("outside-tap");
    };
    const onKey = (e) => {
      if (e.key === "Escape") dismiss("escape");
    };
    document.addEventListener("pointerdown", onDocPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDocPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [show]);

  if (!show || !pos) return null;

  // Position the pill in a fixed-position wrapper so it visually anchors
  // to the card even as the page scrolls. Left/width lifted from the
  // measured anchor rect so the pill matches the card's width on
  // desktop and hugs the safe area on mobile.
  return (
    <div
      className="pointer-events-none fixed z-40 -translate-y-full"
      style={{ top: pos.top, left: pos.left, width: pos.width }}
      data-testid="corridor-nudge"
      role="dialog"
      aria-labelledby="corridor-nudge-title"
    >
      <div className="pointer-events-auto mx-2 rounded-2xl bg-leaf-ink text-cream shadow-botanical px-4 py-3 flex items-start gap-3">
        <div className="shrink-0 mt-0.5">
          <span className="inline-block w-2 h-2 rounded-full bg-[#E9C46A] animate-pulse" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <p
            id="corridor-nudge-title"
            className="font-display text-sm leading-tight"
          >
            {t("result.nudge.title")}
          </p>
          <p className="mt-1 text-[12px] text-cream/80 leading-snug">
            {t("result.nudge.body")}
          </p>
        </div>
        <button
          type="button"
          onClick={() => dismiss("x-tap")}
          aria-label={t("common.close")}
          data-testid="corridor-nudge-dismiss"
          className="shrink-0 -mr-1 -mt-1 w-7 h-7 rounded-full text-cream/70 hover:text-cream hover:bg-cream/10 flex items-center justify-center transition"
        >
          <X className="w-3.5 h-3.5" strokeWidth={1.75} />
        </button>
      </div>
      {/* Downward-pointing tail that visually connects the pill to the card. */}
      <div
        className="mx-auto -mt-0.5 w-8 h-8 rounded-full bg-leaf-ink text-[#E9C46A] flex items-center justify-center shadow"
        aria-hidden
        style={{ width: 28, height: 28 }}
      >
        <ArrowDown className="w-4 h-4" strokeWidth={2} />
      </div>
    </div>
  );
}
